import React, { useState } from 'react';

import styles from './DocumentsList.module.scss';
import { isUploadedDocument as isUploadedDocumentType, LocalDocument, UploadedDocument } from '@app/types/UploadADoc';
import Document from './Document';
import { getEocDocumentTypeDisplayName } from '../../ExchangeOfContracts.model';
import { DndContext, DragStartEvent, DragEndEvent, closestCenter } from '@dnd-kit/core';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { EocDocumentCategory } from '@app/types/ExchangeOfContracts';

interface DocumentsListProps {
    documents: UploadedDocument[];
    clients: unknown[];
    deleteDocument: (id?: string) => Promise<void>;
    getDocumentLink: (id: string) => string;
    reorderDocumentsList: (activeId: string | number, overId: string | number) => void;
}

const DocumentsList: React.FC<DocumentsListProps> = ({
    documents,
    clients,
    deleteDocument,
    getDocumentLink,
    reorderDocumentsList
}) => {
    const [draggingDocumentId, setDraggingDocumentId] = useState<number | string | null>(null);

    const handleDragStart = (dragEvent: DragStartEvent) => {
        setDraggingDocumentId(dragEvent.active.id);
    };

    const handleDragEnd = (dragEvent: DragEndEvent) => {
        const { active, over } = dragEvent;
        if (over && active.id !== over.id) {
            reorderDocumentsList(active.id, over.id);
        }
        setDraggingDocumentId(null);
    };

    return (
        <div className={styles.container}>
            {documents
                .filter(document => document.documentType === EocDocumentCategory.SALES_CONTRACT)
                .map((document, index) => {
                    return (
                        <Document
                            key={document.id}
                            document={document}
                            clients={clients}
                            documentIndex={index}
                            deleteDocument={deleteDocument}
                            documentsCount={documents.length}
                            viewDocumentLink={getDocumentLink(document.document.id)}
                            documentTypeDisplayName={getEocDocumentTypeDisplayName(document.documentType)}
                        />
                    );
                })}
            <DndContext
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
            >
                <SortableContext items={documents} strategy={verticalListSortingStrategy}>
                    {documents
                        .filter(document => document.documentType !== EocDocumentCategory.SALES_CONTRACT)
                        .map((document, index) => {
                            const isUploadedDocument = isUploadedDocumentType(document);
                            const viewDocumentLink = isUploadedDocument
                                ? getDocumentLink(document.document.id)
                                : undefined;

                            const documentTypeDisplayName = getEocDocumentTypeDisplayName(document.documentType);

                            return (
                                <Document
                                    key={isUploadedDocument ? document.id : index}
                                    document={document}
                                    clients={clients}
                                    documentIndex={index}
                                    deleteDocument={deleteDocument}
                                    documentsCount={documents.length}
                                    viewDocumentLink={viewDocumentLink}
                                    documentTypeDisplayName={documentTypeDisplayName}
                                    isDraggable={documents?.length > 1}
                                    isDragDisabled={false}
                                    isDragging={draggingDocumentId === document.id}
                                />
                            );
                        })}
                </SortableContext>
            </DndContext>
        </div>
    );
};

export default DocumentsList;
